import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getBrowserClient } from '@/lib/supabase';
import { useHostessStore } from '@/stores/hostess';
import { useAssignedStores } from '@/hooks/useAssignedStores';
import { useUIStore } from '@/stores/ui';
import {
  CUSTOMER_TYPE_EMOJI,
  formatDate,
  isKanaQuery,
  toKatakana,
  type DashboardCustomerType,
} from '@sakura-grp/lib';
import { ACTIVITY_THRESHOLDS } from '@sakura-grp/database/services';
import { startOfDay, endOfDay } from 'date-fns';
import type { CustomerListItem } from '~/types/customer-detail.types';

/** RPC のタブ値（URL パラメータとは異なる） */
type RpcTab = 'cards' | 'assigned' | 'branch' | 'others' | 'store' | 'non';

/** URL タブ → RPC タブ変換 */
const TAB_TO_RPC: Record<string, RpcTab> = {
  cards: 'cards',
  assigned: 'assigned',
  branch: 'branch',
  others: 'others',
  store: 'store',
  'non-members': 'non',
};

const PAGE_SIZE = 50;

/** store_customers.category → DashboardCustomerType */
function toCustomerType(category: string | null): DashboardCustomerType {
  if (category === 'assigned' || category === 'store' || category === 'non') {
    return category;
  }
  return 'non';
}

export interface TabCustomersPage {
  customers: CustomerListItem[];
  totalRows: number;
  nextPage: number | undefined;
}

interface Filters {
  search: string;
  statusTypes: string[];
  selectedStoreIds: string[];
  dateRange: { from: Date; to: Date } | null;
}

export function useTabCustomers(tab: string, filters: Filters) {
  const { hostess } = useHostessStore();
  const hostessId = hostess?.id;
  const { data: assignedStores } = useAssignedStores();
  const storeFilterInitialized = useUIStore((s) => s.storeFilterInitialized);

  // 店舗フィルターの派生値（Codex レビュー 2026-04-11 案B）
  //
  // - 未初期化時（初回レンダー、useEffect 前）: allStoreIds を使用
  //   これにより初回レンダーで 1 回で正しいクエリが発火する（ちらつき防止）
  // - 初期化済み: selectedStoreIds をそのまま使用
  //   空配列 = 0店舗選択 → クエリ無効化 → プレースホルダー表示
  const storeIds = useMemo(() => {
    const allStoreIds = assignedStores?.map((s) => s.id);
    if (!allStoreIds || allStoreIds.length === 0) return undefined;
    if (!storeFilterInitialized) return allStoreIds;
    // 二重防御: persist された selectedStoreIds に assignedStores 範囲外の ID が
    // 混入していたら RPC に流す前で除外する (useUIStore.initializeStoreFilters と同じ意図)。
    return filters.selectedStoreIds.filter((id) => allStoreIds.includes(id));
  }, [assignedStores, storeFilterInitialized, filters.selectedStoreIds]);

  const rpcTab = TAB_TO_RPC[tab] || 'cards';

  // ステータスフィルター: good+poor 両方選択 or 空 = フィルターなし
  const hasGood = filters.statusTypes.includes('good');
  const hasPoor = filters.statusTypes.includes('poor');
  const statusParam = (hasGood && hasPoor) || (!hasGood && !hasPoor)
    ? undefined
    : hasGood ? 'good' : 'poor';

  const searchTrimmed = filters.search.trim();
  const searchParam = searchTrimmed || undefined;
  // フリガナ検索ゲート: カナ (ひらがな/カタカナ) を含み 2 文字以上のとき、
  // toKatakana で正規化したカナ語を p_search_kana として渡す。
  // - ASCII のみ (AB) や漢字 (山田) は kana 経路に行かない
  // - 「マミ」「アキ」等の 2 文字カナ名を引けるよう min 2 文字
  const searchKanaParam =
    searchTrimmed.length >= 2 && isKanaQuery(searchTrimmed)
      ? toKatakana(searchTrimmed)
      : undefined;
  // JST 日付をローカル時刻の開始/終了で UTC 境界に変換
  // startOfDay/endOfDay はローカル TZ (JST) で計算 → toISOString で UTC に変換
  const dateFromParam = filters.dateRange?.from
    ? startOfDay(filters.dateRange.from).toISOString()
    : undefined;
  const dateToParam = filters.dateRange?.to
    ? endOfDay(filters.dateRange.to).toISOString()
    : undefined;

  return useInfiniteQuery<TabCustomersPage>({
    queryKey: ['tab-customers', rpcTab, hostessId, storeIds, searchParam, searchKanaParam, statusParam, dateFromParam, dateToParam],
    queryFn: async ({ pageParam }) => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase.rpc('get_dashboard_customers', {
        p_hostess_id: hostessId!,
        p_store_ids: storeIds!,
        p_tab: rpcTab,
        p_search: searchParam,
        p_search_kana: searchKanaParam,
        p_status: statusParam,
        p_date_from: dateFromParam,
        p_date_to: dateToParam,
        p_page: pageParam as number,
        p_page_size: PAGE_SIZE,
      });

      if (error) throw new Error(`ダッシュボード取得エラー: ${error.message}`);

      const rows = data || [];
      const totalRows = rows[0]?.total_rows ?? 0;

      const customers: CustomerListItem[] = rows.map((row) => {
        const total = row.total_count ?? 0;
        const status = ACTIVITY_THRESHOLDS.evaluate(total);
        const customerType = toCustomerType(row.customer_category);
        // 名刺タブ以外: GREATEST(last_activity_date, last_visit_date, registered_date)
        const sortDate = rpcTab === 'cards'
          ? row.registered_date
          : [row.last_activity_date, row.last_visit_date, row.registered_date]
              .filter(Boolean)
              .sort()
              .pop() ?? row.registered_date;
        return {
          id: row.customer_id,
          type: customerType,
          registeredDate: formatDate(sortDate),
          lastVisitDate: formatDate(row.last_visit_date),
          storeName: row.store_name || '不明',
          storeId: row.store_id || undefined,
          hostessName: row.hostess_name || undefined,
          hostessId: row.hostess_id || undefined,
          statusType: status,
          statusLabel: status === 'good' ? '営業良好' : '営業不足',
          activities: {
            phone: row.phone_count,
            message: row.message_count,
            note: row.letter_count,
            visit: row.visit_count,
          },
          restrictions: {
            phone: row.allow_phone === false,
            message: row.allow_message === false,
            dm: row.allow_dm === false,
            visit: row.allow_visit === false,
          },
          isMyAssigned: row.is_my_assigned ?? false,
          isBranch: row.is_branch ?? false,
          emoji: CUSTOMER_TYPE_EMOJI[customerType],
          name: row.customer_name || '不明',
          company: row.company_name || '',
          linkTo: `/customers/${row.customer_id}`,
        };
      });

      const loadedSoFar = (pageParam as number) * PAGE_SIZE;
      const nextPage = loadedSoFar < totalRows ? (pageParam as number) + 1 : undefined;

      return { customers, totalRows, nextPage };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!hostessId && !!storeIds && storeIds.length > 0,
  });
}
